import {
    getOrganizationScopes,
    IClauseChecklist,
    IFilter,
    IOrganization,
    IScope,
} from "@/api";
import { ScopeLink } from "@/components/admin/table/cells/ScopeLink";
import { ClauseChecklistChip } from "@/components/shared/chips/ClauseChecklistChip";
import { Table } from "@/components/shared/table/Table";
import { formatDate } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ViewAllScopesButton } from "../buttons/ViewAllScopesButton";

const columns: GridColDef<IScope>[] = [
    {
        field: "document_title",
        headerName: "Scope",
        display: "flex",
        renderCell: (params) => {
            return <ScopeLink scope={params.row} />;
        },
    },
    {
        field: "clause_checklist",
        headerName: "Clause Checklist",
        display: "flex",
        valueGetter: (value: IClauseChecklist) => {
            return value.name;
        },
        renderCell: (params) => {
            return (
                <ClauseChecklistChip
                    clauseChecklist={params.row.clause_checklist.name}
                />
            );
        },
    },

    {
        field: "created_at",
        headerName: "Date Uploaded",
        display: "flex",
        type: "date",
        valueFormatter: (value) => {
            return formatDate(value, "PPp");
        },
    },
];

interface IProps {
    /**
     * the organization to display the recent scopes for
     */
    organization: IOrganization;
}

/**
 * a table that displays the recent scopes for an organization
 */
export const RecentScopesTable = ({ organization }: IProps) => {
    const autoSizeOptions = {
        columns: ["document_title", "clause_checklist", "created_at"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const [rows, setRows] = useState<IScope[]>([]);

    const filter: IFilter = {
        sort_by: "created_at",
        order: "desc",
    };

    const page = 1;
    const size = 5;

    // Fetch 5 most recent scopes
    const { data, isPending } = useQuery({
        queryKey: ["admin-organization-details-recent-scopes", organization.id],
        queryFn: () =>
            getOrganizationScopes(organization.id, page, size, filter),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    return (
        <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" mx={2}>
                <Typography variant="h6">Recent Scopes</Typography>
                <ViewAllScopesButton organization={organization} />
            </Stack>
            <Box sx={{ mt: 2, mx: 1 }}>
                <Table
                    loading={isPending}
                    rows={rows}
                    columns={columns}
                    autoSize
                    autosizeOptions={autoSizeOptions}
                    hideFooter
                />
            </Box>
        </Box>
    );
};
